import { useCallback, useEffect, useMemo, useState } from 'react';

const DEFAULT_MIN_WIDTH = 64;

// Saved choices, limited to columns that still exist
const readStored = (storageKey, columnKeys, minWidth) => {
  try {
    const parsed = JSON.parse(localStorage.getItem(storageKey) || 'null');
    if (!parsed || typeof parsed !== 'object') return {};
    const visible = {};
    const widths = {};
    columnKeys.forEach((key) => {
      if (typeof parsed.visible?.[key] === 'boolean') visible[key] = parsed.visible[key];
      if (Number.isFinite(parsed.widths?.[key])) widths[key] = Math.max(minWidth, parsed.widths[key]);
    });
    return { visible, widths };
  } catch (error) {
    return {};
  }
};

/**
 * Which columns a data table shows and how wide they are, remembered in this
 * browser. Only the user's departures from `defaultVisible` are stored, so new
 * defaults still reach everyone who didn't change that column.
 *  - columns: { [key]: { width } } in display order
 *  - autoHidden: { [key]: true } columns hidden for now (e.g. always empty under the current filter)
 */
const useTableColumns = ({ storageKey, columns, defaultVisible, autoHidden = {}, minWidth = DEFAULT_MIN_WIDTH }) => {
  const columnKeys = useMemo(() => Object.keys(columns), [columns]);
  const [visibleColumns, setVisibleColumns] = useState(() => ({
    ...defaultVisible,
    ...readStored(storageKey, columnKeys, minWidth).visible,
  }));
  const [columnWidths, setColumnWidths] = useState(() => readStored(storageKey, columnKeys, minWidth).widths || {});

  useEffect(() => {
    const changed = columnKeys.filter((key) => Boolean(visibleColumns[key]) !== Boolean(defaultVisible[key]));
    try {
      localStorage.setItem(storageKey, JSON.stringify({
        visible: Object.fromEntries(changed.map((key) => [key, Boolean(visibleColumns[key])])),
        widths: columnWidths,
      }));
    } catch (error) {
      // Storage may be unavailable (private mode); the choices still apply now.
    }
  }, [storageKey, columnKeys, defaultVisible, visibleColumns, columnWidths]);

  const toggleColumn = useCallback((key) => {
    setVisibleColumns((prev) => {
      // Keep at least one column on screen
      if (prev[key] && columnKeys.filter((item) => prev[item]).length <= 1) return prev;
      return { ...prev, [key]: !prev[key] };
    });
  }, [columnKeys]);

  const showAllColumns = useCallback(() => {
    setVisibleColumns(Object.fromEntries(columnKeys.map((key) => [key, true])));
  }, [columnKeys]);

  const resetColumns = useCallback(() => {
    setVisibleColumns(defaultVisible);
    setColumnWidths({});
  }, [defaultVisible]);

  const setColumnWidth = useCallback((key, width) => {
    setColumnWidths((prev) => ({ ...prev, [key]: Math.max(minWidth, Math.round(width)) }));
  }, [minWidth]);

  const resetColumnWidth = useCallback((key) => {
    setColumnWidths((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const autoHiddenSignature = columnKeys.filter((key) => autoHidden[key]).join('|');
  const visibleKeys = useMemo(() => {
    const hidden = new Set(autoHiddenSignature.split('|'));
    return columnKeys.filter((key) => visibleColumns[key] && !hidden.has(key));
  }, [columnKeys, visibleColumns, autoHiddenSignature]);

  const getColumnWidth = useCallback((key) => columnWidths[key] || columns[key].width, [columnWidths, columns]);

  return {
    visibleColumns,
    visibleKeys,
    toggleColumn,
    showAllColumns,
    resetColumns,
    getColumnWidth,
    setColumnWidth,
    resetColumnWidth,
  };
};

export default useTableColumns;
